import { requests } from '@fable/api'
import { Book } from '@fable/types'
import { AxiosResponse } from 'axios'
import { useErrorModal, useTypedSelector } from 'hooks'
import { useEffect } from 'react'
import { useQuery, UseQueryResult } from 'react-query'
import { useLocation } from 'react-router-dom'
import { getSlug, isUUID } from 'utils'

export const getBookDetailId = (pathname: string) => {
  let id = getSlug(pathname)
  const lastDashIndex = id.lastIndexOf('-')
  const isISBN = lastDashIndex !== -1 && !isUUID(id)
  if (isISBN) id = id.substring(lastDashIndex + 1)

  return id
}

export const useLoadBookDetail = () => {
  const { pathname } = useLocation()
  const { isAuthenticated } = useTypedSelector(({ auth }) => auth)
  let id = getBookDetailId(pathname)

  const bookQuery = useQuery(['book-detail'], () => requests.getBook(id), {
    enabled: !!id,
  })

  const book = bookQuery.data?.data?.response as Book
  const bookId = book?.id

  useQuery(
    ['owned-books'],
    () => requests.getOwnedBooks({ includePreorder: true }),
    {
      enabled: !!book && !!isAuthenticated,
    }
  )

  const similarBooksQuery: UseQueryResult<AxiosResponse> = useQuery(
    ['similarBooks', bookId],
    () => requests.getSimilarBooks(bookId),
    { enabled: !!bookId }
  )

  const clubSuggestionsQuery: UseQueryResult<AxiosResponse> = useQuery(
    ['club-suggestion', bookId],
    () => requests.getClubSuggestion(bookId),
    { enabled: false } // KG: disabled for perf reasons - https://fablegroup.atlassian.net/browse/WEB-1168
  )

  const isValidId = book?.id === id || book?.isbn === id

  useEffect(() => {
    if (!bookQuery.isFetching && !isValidId && !bookQuery.isError) {
      bookQuery.refetch()
    }
  }, [book?.isbn, bookQuery, isValidId])

  useErrorModal(bookQuery.error as Error)

  return { bookQuery, book, similarBooksQuery, clubSuggestionsQuery }
}
